import React, { useState, useEffect, useMemo } from "react";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import SearchableTable from "../../../components/Tables/SearchableTable";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import { ProductColumns } from "../../../assets/Columns/ProductColumns";
import CreateProductModal from "../../../components/Modals/CreateProductModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../../../store/Slices/ProductSlice";
import DeleteModal from "../../../components/Modals/DeleteModal";
import { DeleteProductsByIdApi } from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import EditCustomerModal from "../../../components/Modals/EditCustomerModal";
import EditProductModal from "../../../components/Modals/EditProductModal";
import { useNavigate } from "react-router-dom";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import ExportExcelButton from "../../../components/Buttons/ExportExcelButton";
import ExportToExcelButton from "../../../utils/ExportToExcel";
import PrintButton from "../../../components/Buttons/PrintButton";
import AddOpeningQtyItemModal from "../../../components/Modals/AddOpeningQtyItemModal";
import CompanySelector from "../../../components/Selector/CompanySelector";
import AdjustItemQtyModal from "../../../components/Modals/AdjustItemQtyModal";
import ItemQtyLogModal from "../../../components/Modals/ItemQtyLogModal";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import MetricCard from "../../../components/Cards/MetricCard";

const getAppBasePath = () =>
  Number(localStorage.getItem("role")) === 1 ? "/admin" : "/branch";

const Products = () => {
  const [SearchText, setSearchText] = useState("");
  const [OpenModal, setOpenModal] = useState(false);
  const [OpenOpeningQtyModal, setOpenOpeningQtyModal] = useState(false);
  const [Loading, setLoading] = useState(false);
  const [OpenEditModal, setOpenEditModal] = useState(false);
  const [OpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [OpenQtyAdjustModal, setOpenQtyAdjustModal] = useState(false);
  const [OpenQtyLogModal, setOpenQtyLogModal] = useState(false);
  const [Selected, setSelected] = useState({});
  const [selectedCompany, setSelectedCompany] = useState("");

  const dispatch = useDispatch();
  const ItemsState = useSelector((state) => state.ItemsState);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  const itemsByCompany = useMemo(() => {
    const data = ItemsState.data || [];
    if (!selectedCompany || selectedCompany === "") return data;
    const companyId = selectedCompany._id?.toString();
    return data.filter((product) => {
      const pid = product.companyId?._id ?? product.companyId;
      return pid?.toString() === companyId;
    });
  }, [ItemsState.data, selectedCompany]);

  const displayedItems = useMemo(
    () => filterTableRowsBySearch(itemsByCompany, SearchText, ProductColumns),
    [itemsByCompany, SearchText]
  );

  const summary = useMemo(() => {
    if (!displayedItems.length) {
      return {
        in_qty: 0,
        out_qty: 0,
        qty: 0,
      };
    }

    return displayedItems.reduce(
      (acc, product) => ({
        in_qty: acc.in_qty + (product.in_qty || 0),
        out_qty: acc.out_qty + (product.out_qty || 0),
        qty: acc.qty + (product.qty || 0),
      }),
      {
        in_qty: 0,
        out_qty: 0,
        qty: 0,
      }
    );
  }, [displayedItems]);

  return (
    <BodyWrapper>
      <Header title="Items" desc="Overview of your items management">
        <CustomBtn
          title={"Add Opening Qty"}
          onClick={() => {
            setOpenOpeningQtyModal(true);
          }}
        />
        <CustomBtn
          title={"Stock Qty Log"}
          onClick={() => {
            navigate(`${getAppBasePath()}/stock-qty-log`);
          }}
        />
        <CustomBtn
          title={"Item Summary"}
          onClick={() => {
            navigate(`${getAppBasePath()}/items/summary`);
          }}
        />
        <CustomBtn
          title={"Add New Item"}
          onClick={() => {
            setOpenModal(true);
          }}
        />
      </Header>
      <div className="flex justify-end gap-x-2">
        <PrintButton
          onClick={() => {
            navigate(`${getAppBasePath()}/items-report`);
          }}
          title="Items Report"
        />

        <ExportToExcelButton data={displayedItems} fileName={"Items"} />
      </div>

      <div className="w-full mt-10 flex flex-col items-center">
        {ItemsState.loading ? (
          <div className="flex flex-1 justify-center items-center">
            <FetchingLoading />
          </div>
        ) : (
          ItemsState.data.length !== 0 && (
            <div className="w-full">
              <div className="mb-10 flex flex-col gap-3 max680:w-full md:flex-row md:items-start md:gap-4">
                <div className="flex-1 min-w-0 md:max-w-md">
                  <CompanySelector
                    SelectedSupplier={selectedCompany}
                    setSelectedSupplier={setSelectedCompany}
                  />
                </div>
                {(selectedCompany && selectedCompany !== "") && (
                  <CustomBtn
                    title={"All companies"}
                    onClick={() => setSelectedCompany("")}
                  />
                )}
              </div>
              <SearchableTable
                setOpenEditModal={setOpenEditModal}
                setOpenDeleteModal={setOpenDeleteModal}
                setSelected={setSelected}
                setOpenQtyAdjustModal={setOpenQtyAdjustModal}
                setOpenQtyLogModal={setOpenQtyLogModal}
                SearchPlaceholder={"Search items..."}
                SearchText={SearchText}
                setSearchText={setSearchText}
                CurrentData={itemsByCompany}
                Columns={ProductColumns}
              />
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                <MetricCard
                  title="Total In-Quantity"
                  value={summary.in_qty.toLocaleString("en-PK")}
                  subtitle="Total received stock quantity"
                  tone="success"
                />
                <MetricCard
                  title="Total Out-Quantity"
                  value={summary.out_qty.toLocaleString("en-PK")}
                  subtitle="Total issued stock quantity"
                  tone="danger"
                />
                <MetricCard
                  title="Total Quantity"
                  value={summary.qty.toLocaleString("en-PK")}
                  subtitle="Net available stock quantity"
                  tone="neutral"
                />
              </div>
            </div>
          )
        )}
      </div>

      {OpenModal && (
        <CreateProductModal Open={OpenModal} setOpen={setOpenModal} />
      )}
      {OpenOpeningQtyModal && (
        <AddOpeningQtyItemModal
          Open={OpenOpeningQtyModal}
          setOpen={setOpenOpeningQtyModal}
        />
      )}
      {OpenEditModal && (
        <EditProductModal
          Open={OpenEditModal}
          setOpen={setOpenEditModal}
          currentProduct={Selected}
        />
      )}
      {OpenQtyAdjustModal && (
        <AdjustItemQtyModal
          Open={OpenQtyAdjustModal}
          setOpen={setOpenQtyAdjustModal}
          product={Selected}
        />
      )}
      {OpenQtyLogModal && (
        <ItemQtyLogModal
          Open={OpenQtyLogModal}
          setOpen={setOpenQtyLogModal}
          product={Selected}
        />
      )}
      {OpenDeleteModal && (
        <DeleteModal
          Open={OpenDeleteModal}
          setOpen={setOpenDeleteModal}
          Loading={Loading}
          onSubmit={async () => {
            setLoading(true);
            try {
              const response = await DeleteProductsByIdApi(Selected._id);
              if (response.data.success) {
                setOpenDeleteModal(false);
                SuccessToast("Product added successfully");
                dispatch(fetchProducts());
              } else {
                ErrorToast(response.data.error?.msg);
              }
            } catch (err) {
              console.log("err: ", err);
              ErrorToast("Failed to add Product");
            }
            setLoading(false);
          }}
        />
      )}
    </BodyWrapper>
  );
};

export default Products;
